import { type, type Type } from 'arktype';
import type { AbsencePortion } from './absence-payload.model';
import type { ReadEnvelope } from './read-envelope.model';
import type { PersonRef } from './timetracking-person.model';

export const absenceStatuses = ['requested', 'approved', 'reported', 'rejected', 'withdrawn'] as const;
export type AbsenceStatus = (typeof absenceStatuses)[number];
export const absenceStatusSchema = type.enumerated(...absenceStatuses);

export const CALENDAR_ABSENCE_STATUSES: readonly Extract<AbsenceStatus, 'requested' | 'approved' | 'reported'>[] = [
  'requested',
  'approved',
  'reported',
];
export type CalendarAbsenceStatus = (typeof CALENDAR_ABSENCE_STATUSES)[number];
export const calendarAbsenceStatusSchema: Type<CalendarAbsenceStatus> = type.enumerated(...CALENDAR_ABSENCE_STATUSES);

export type AbsenceHalf = 'am' | 'pm' | 'none';

export interface AbsenceTypeVisual {
  typeKey: string;
  label: string;
  icon: string | null;
  color: string | null;
}

export interface AbsenceEntryDay {
  date: string;
  countedDays: number;
  half: AbsenceHalf;
  /** Wall-clock "HH:mm", not an instant. */
  windowStart?: string;
  windowEnd?: string;
}

export interface AbsenceEntry extends AbsenceTypeVisual {
  entryId: string;
  absenceId: string | null;
  requestId: string | null;
  personId: string;
  status: AbsenceStatus;
  pendingKind?: 'update' | 'remove';
  dateFrom: string;
  dateTo: string;
  portion: AbsencePortion;
  half: AbsenceHalf;
  hours: number | null;
  countedDays: number;
  days?: AbsenceEntryDay[];
  requestedAt: string | null;
  reportedAt?: string;
  approvers?: PersonRef[];
  decidedBy?: PersonRef;
  decidedAt?: string;
  decisionComment?: string | null;
  canWithdraw: boolean;
}

export interface AbsenceTypeOption extends AbsenceTypeVisual {
  requiresApproval: boolean;
  reported: boolean;
  balanceType: string | null;
  allowedPortions?: AbsencePortion[];
}

export interface AbsenceSumBucket {
  key: string;
  countedDays: number;
}

export type AbsenceSums = { groupBy: 'type'; buckets: AbsenceSumBucket[] } | { groupBy: 'month'; buckets: AbsenceSumBucket[] };

export interface AbsencesResponse extends ReadEnvelope {
  total: number;
  items: AbsenceEntry[];
  types?: AbsenceTypeOption[];
  sums?: AbsenceSums;
}
